import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetDealStagesQuery } from "@/store/rest_api";
import { CallDispositionAction } from "@/enum/CallDispositionAction";
import { CallDispositionOutcome } from "@/enum/CallDispositionOutcome";
import { DISPOSITION_ACTION_LABELS, DISPOSITION_OUTCOME_LABELS } from "@/util/dispositions";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PRESET_COLORS = [
  "#22C55E", "#EF4444", "#F59E0B", "#F97316", "#3B82F6",
  "#6B7280", "#DC2626", "#10B981", "#8B5CF6", "#06B6D4",
  "#EC4899", "#14B8A6", "#A855F7", "#84CC16",
];

const NO_STAGE = "NO_STAGE";

export interface DispositionFormData {
  name: string;
  description: string;
  color: string;
  requiresFollowup: boolean;
  action: CallDispositionAction;
  outcome: CallDispositionOutcome;
  dealStageId: string | null;
}

interface DispositionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: DispositionFormData;
  onFormChange: (form: DispositionFormData) => void;
  onSave: () => void;
  saving: boolean;
  isEdit: boolean;
}

const DispositionFormDialog: React.FC<DispositionFormDialogProps> = ({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSave,
  saving,
  isEdit,
}) => {
  const { data: dealStages = [] } = useGetDealStagesQuery({}, { skip: !open });

  const update = (patch: Partial<DispositionFormData>) => {
    onFormChange({ ...form, ...patch });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Disposition" : "New Disposition"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="e.g. Interested, Left Voicemail"
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="When to use this disposition"
            />
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => update({ color })}
                  className="h-7 w-7 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: color,
                    borderColor: form.color === color ? "white" : "transparent",
                    boxShadow: form.color === color ? `0 0 0 2px ${color}` : "none",
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Label className="text-xs text-muted-foreground">Custom:</Label>
              <Input
                type="color"
                value={form.color}
                onChange={(e) => update({ color: e.target.value })}
                className="h-7 w-10 p-0 border-0 cursor-pointer"
              />
              <span className="text-xs text-muted-foreground font-mono">
                {form.color}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Requires Follow-up</Label>
              <p className="text-xs text-muted-foreground">
                Starts the "Follow-up task after a call" workflow, which gives the agent a task due the next day. Change it in Workflows.
              </p>
            </div>
            <Switch
              checked={form.requiresFollowup}
              onCheckedChange={(checked) => update({ requiresFollowup: checked })}
            />
          </div>
          <div className="space-y-2">
            <Label>When Saved</Label>
            <Select
              value={form.action}
              onValueChange={(value) => update({ action: value as CallDispositionAction })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(CallDispositionAction).map((action) => (
                  <SelectItem key={action} value={action}>
                    {DISPOSITION_ACTION_LABELS[action]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Runs once when an agent saves this disposition on a call. A dialer callback without a time comes back the
              next day. Outside the dialer, a callback becomes a task.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Counts As</Label>
              <Select
                value={form.outcome}
                onValueChange={(value) => update({ outcome: value as CallDispositionOutcome })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(CallDispositionOutcome).map((outcome) => (
                    <SelectItem key={outcome} value={outcome}>
                      {DISPOSITION_OUTCOME_LABELS[outcome]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Used by dialer reports</p>
            </div>
            <div className="space-y-2">
              <Label>Move Deal To</Label>
              <Select
                value={form.dealStageId ?? NO_STAGE}
                onValueChange={(value) => update({ dealStageId: value === NO_STAGE ? null : value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STAGE}>No stage change</SelectItem>
                  {dealStages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Moves the lead's open deal</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispositionFormDialog;
